import { Injectable } from '@angular/core';
import { FormGroup, FormControl, ValidatorFn } from '@angular/forms';
import { chain, merge, pickBy, isEqual, identity, pick, isArray } from 'lodash';

// @dynamic
@Injectable()
export class FormHelper {
  public static setField(field: string, formState: any = '', validator?: ValidatorFn | ValidatorFn[] | null): { [key: string]: FormControl } {
    return { [field]: new FormControl(formState, validator) };
  }

  public static setDisabledField(field: string): { [key: string]: FormControl } {
    return FormHelper.setField(field, { value: '', disabled: true });
  }

  public static setFormGroupFields(
    fields: string[],
    disabled = false,
    validator?: { [field: string]: ValidatorFn | ValidatorFn[] | null },
  ): FormGroup {
    const setField = (fld: string): { [key: string]: FormControl } => {
      if (disabled) {
        return FormHelper.setDisabledField(fld);
      } else {
        if (validator && validator.hasOwnProperty(fld)) {
          return FormHelper.setField(fld, '', validator[fld]);
        } else {
          return FormHelper.setField(fld);
        }
      }
    };
    return new FormGroup(chain(fields).map(setField).reduce(merge, {}).value());
  }

  public static getNonEmptyValues(formValue: any): any {
    return pickBy(formValue, (value) => {
      if (isArray(value)) {
        return value.length > 0;
      }
      return identity(value);
    });
  }

  public static getNonEmptyAndChangedValues(formValue: any, controlValue: any): any {
    return pickBy(formValue, (value: any, key: string): boolean => !!value && !isEqual(controlValue[key], value));
  }

  public static getTruthyValues(object: any): any {
    return chain(object).pickBy(identity).keys().value();
  }

  public static getDifference(a: any, b: any) {
    return pick(
      b,
      Object.keys(a).filter((k) => a[k] !== b[k]),
    );
  }

  static getChangedValues(current: any, initial: any): any {
    const changed: any = Array.isArray(current) ? [] : {};
    Object.keys(current).forEach((key) => {
      const currentVal = current[key];
      const initialVal = initial[key];

      if (Array.isArray(currentVal) && Array.isArray(initialVal)) {
        const nested = FormHelper.getChangedValues(currentVal, initialVal);
        if (Array.isArray(nested) ? nested.length > 0 : Object.keys(nested).length > 0) {
          changed[key] = nested;
        }
        return;
      }

      if (FormHelper.isPlainObject(currentVal) && FormHelper.isPlainObject(initialVal)) {
        const nested = FormHelper.getChangedValues(currentVal, initialVal);
        if (Object.keys(nested).length > 0) {
          changed[key] = nested;
        }
        return;
      }

      let normalizedCurrent = currentVal;
      let normalizedInitial = initialVal;

      if (normalizedCurrent instanceof Date) {
        normalizedCurrent = normalizedCurrent.toISOString();
      }
      if (normalizedInitial instanceof Date) {
        normalizedInitial = normalizedInitial.toISOString();
      }

      const isDateString = (val: any) => typeof val === 'string' && !isNaN(Date.parse(val));
      if (isDateString(normalizedCurrent) && isDateString(normalizedInitial)) {
        normalizedCurrent = new Date(normalizedCurrent).toISOString();
        normalizedInitial = new Date(normalizedInitial).toISOString();
      }

      if (!isEqual(normalizedCurrent, normalizedInitial)) {
        changed[key] = currentVal;
      }
    });
    return changed;
  }

  private static isPlainObject(value: any): boolean {
    return value !== null && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date);
  }
}
